// Shared aggregation logic for KONTROL score feedback -- used by the manager
// dashboard's "Team feedback" tab and the admin feedback responses overview.
import { labelForOption } from "../ScoreFeedback";
import type { KontrolScoreFeedbackAnswers, KontrolScoreFeedbackQuestion } from "../../types/scores";

// Below this many respondents, a per-question breakdown isn't shown -- with a
// handful of employees an aggregate can still single someone out.
export const MIN_FEEDBACK_RESPONDENTS = 3;

export function isScaleFeedbackQuestion(question: KontrolScoreFeedbackQuestion) {
  if (question.input_type !== "single" || question.answer_options.length < 3) return false;
  const middle = question.answer_options.slice(1, -1);
  return middle.every((option, index) => option.value === String(index + 1));
}

export type FeedbackAggregate = {
  question: KontrolScoreFeedbackQuestion;
  respondentCount: number;
  breakdown: { label: string; count: number; pct: number }[];
  conclusion: string;
  // Only set for scale questions: average answer position as a 0-100 percent of the scale.
  scalePct: number | null;
};

export function aggregateFeedbackQuestion(
  question: KontrolScoreFeedbackQuestion,
  answers: KontrolScoreFeedbackAnswers[],
  lang: "en" | "de",
  L: (en: string, de: string) => string
): FeedbackAggregate {
  const values = answers
    .map((a) => a[question.question_code])
    .filter((v): v is string | string[] => (Array.isArray(v) ? v.length > 0 : Boolean(v && String(v).trim())));

  const respondentCount = values.length;

  if (respondentCount < MIN_FEEDBACK_RESPONDENTS) {
    return {
      question,
      respondentCount,
      breakdown: [],
      conclusion: L("Not enough responses yet.", "Noch nicht genug Antworten."),
      scalePct: null,
    };
  }

  if (question.input_type === "text") {
    return {
      question,
      respondentCount,
      breakdown: [],
      conclusion: L(
        `${respondentCount} of ${answers.length} employees added a note.`,
        `${respondentCount} von ${answers.length} Mitarbeitenden haben eine Notiz hinzugefügt.`
      ),
      scalePct: null,
    };
  }

  const counts = new Map<string, number>();
  for (const value of values) {
    const items = Array.isArray(value) ? value : [value];
    for (const item of items) {
      counts.set(item, (counts.get(item) ?? 0) + 1);
    }
  }

  const breakdown = question.answer_options
    .map((option) => {
      const count = counts.get(option.value) ?? 0;
      return { label: labelForOption(option, lang), count, pct: Math.round((count / respondentCount) * 100) };
    })
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);

  if (isScaleFeedbackQuestion(question)) {
    const positions = values
      .map((v) => question.answer_options.findIndex((o) => o.value === v))
      .filter((i) => i >= 0);
    const maxIndex = question.answer_options.length - 1;
    const avgIndex = positions.reduce((sum, i) => sum + i, 0) / positions.length;
    const pct = Math.round((avgIndex / maxIndex) * 100);
    const firstLabel = labelForOption(question.answer_options[0], lang);
    const lastLabel = labelForOption(question.answer_options[maxIndex], lang);

    const conclusion =
      pct < 40
        ? L(
            `${pct}% average agreement -- trending toward "${firstLabel}".`,
            `${pct}% durchschnittliche Zustimmung -- Tendenz zu "${firstLabel}".`
          )
        : pct > 70
        ? L(
            `${pct}% average agreement -- trending toward "${lastLabel}".`,
            `${pct}% durchschnittliche Zustimmung -- Tendenz zu "${lastLabel}".`
          )
        : L(
            `${pct}% average agreement -- mixed responses across the team.`,
            `${pct}% durchschnittliche Zustimmung -- gemischte Antworten im Team.`
          );

    return { question, respondentCount, breakdown, conclusion, scalePct: pct };
  }

  const top = breakdown.slice(0, 2);
  const conclusion = top.length
    ? L(
        `Most common: ${top.map((row) => `"${row.label}" (${row.pct}%)`).join(", ")}.`,
        `Am häufigsten: ${top.map((row) => `"${row.label}" (${row.pct}%)`).join(", ")}.`
      )
    : L("No selections yet.", "Noch keine Auswahl.");

  return { question, respondentCount, breakdown, conclusion, scalePct: null };
}
